import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import SimpleBar from "simplebar-react";
import MetisMenu from "metismenujs";
import { withRouter } from "react-router-dom";
import { Link } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { formatCount, translateLabel } from "../../helpers/language_helper";
import { getCachedMenu, fetchMenu } from "../../helpers/menu_cache";

const SidebarContent = props => {
  const language = props.i18n ? props.i18n.language : undefined;

  // paint whatever is cached on the very first render, so a refresh or a
  // navigation shows the menu immediately instead of an empty sidebar
  const [rolemodules, setRoleModule] = useState(() => getCachedMenu(language) || []);
  const ref = useRef();
  const metisMenu = useRef(null);

  // load / revalidate the menu (cache first, network in the background)
  useEffect(() => {
    let cancelled = false;

    const cached = getCachedMenu(language);
    if (cached && cached.length) setRoleModule(cached);

    fetchMenu(language)
      .then(modules => {
        if (!cancelled && Array.isArray(modules)) setRoleModule(modules);
      })
      .catch(err => {
        console.log(err);
      });

    return () => {
      cancelled = true;
    };
  }, [language]);

  // (re)initialise MetisMenu once the items are actually in the DOM, and
  // highlight the entry matching the current route
  useEffect(() => {
    if (!rolemodules.length) return undefined;

    const ul = document.getElementById("side-menu");
    if (!ul) return undefined;

    // tear the previous instance down first, otherwise every remount stacks
    // another set of click handlers on the same element
    if (metisMenu.current && typeof metisMenu.current.dispose === "function") {
      try {
        metisMenu.current.dispose();
      } catch (e) {
        /* already detached */
      }
    }
    metisMenu.current = new MetisMenu("#side-menu");

    const pathName = props.location.pathname;
    const items = ul.getElementsByTagName("a");
    for (let i = 0; i < items.length; ++i) {
      if (pathName === items[i].pathname) {
        activateParentDropdown(items[i]);
        break;
      }
    }

    return undefined;
  }, [rolemodules, props.location.pathname]);

  // dispose on unmount (the layout remounts on every navigation)
  useEffect(() => () => {
    if (metisMenu.current && typeof metisMenu.current.dispose === "function") {
      try {
        metisMenu.current.dispose();
      } catch (e) {
        /* already detached */
      }
      metisMenu.current = null;
    }
  }, []);

  // recalculate the scrollbar when the menu actually changes, not on every
  // single render as before
  useEffect(() => {
    if (ref.current && typeof ref.current.recalculate === "function") {
      ref.current.recalculate();
    }
  }, [rolemodules, language]);

  function scrollElement(item) {
    if (item) {
      const currentPosition = item.offsetTop;
      if (currentPosition > window.innerHeight) {
        ref.current.getScrollElement().scrollTop = currentPosition - 300;
      }
    }
  }

  function activateParentDropdown(item) {
    item.classList.add("active");
    const parent = item.parentElement;
    const parent2El = parent.childNodes[1];
    if (parent2El && parent2El.id !== "side-menu") {
      parent2El.classList.add("mm-show");
    }

    if (parent) {
      parent.classList.add("mm-active");
      const parent2 = parent.parentElement;

      if (parent2) {
        parent2.classList.add("mm-show"); // ul tag

        const parent3 = parent2.parentElement; // li tag

        if (parent3) {
          parent3.classList.add("mm-active"); // li
          parent3.childNodes[0].classList.add("mm-active"); //a
          const parent4 = parent3.parentElement; // ul
          if (parent4) {
            parent4.classList.add("mm-show"); // ul
            const parent5 = parent4.parentElement;
            if (parent5) {
              parent5.classList.add("mm-show"); // li
              parent5.childNodes[0].classList.add("mm-active"); // a tag
            }
          }
        }
      }
      scrollElement(item);
      return false;
    }
    scrollElement(item);
    return false;
  }

  return (
    <React.Fragment>
      <SimpleBar className="h-100" ref={ref}>
      <div id="sidebar-menu">
  <ul className="metismenu list-unstyled" id="side-menu">
    {rolemodules.map((menu, i) => {
      const notifyBadgeStyle = {
        backgroundColor: '#8b61a1',
        color: 'white',
        borderRadius: '999px',
        padding: '2px 8px',
        fontSize: '12px',
        minWidth: '20px',
        textAlign: 'center',
        fontWeight: 'bold',
        // logical margin so the badge stays on the far side of the label in
        // both English (LTR) and Arabic (RTL)
        marginInlineStart: '8px',
        display: 'inline-block',
      };

      if (!menu.hierarchy) {
        return (
          <li key={`main_${i}`}>
            <Link to={`/${menu.link}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>
                <i className={menu.icon}></i> {translateLabel(menu.label)}
              </span>
              {menu.is_notify_count > 0 && (
                <span style={notifyBadgeStyle}>{formatCount(menu.is_notify_count)}</span>
              )}
            </Link>
          </li>
        );
      } else {
        return (
          <li key={`main_${i}`}>
            <Link to="/#" className="has-arrow" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>
                <i className={menu.icon}></i> {translateLabel(menu.label)}
              </span>
              {menu.is_notify_count > 0 && (
                <span style={notifyBadgeStyle}>{formatCount(menu.is_notify_count)}</span>
              )}
            </Link>
            <ul className="sub-menu" key={`ul_${i}`}>
              {menu?.childrensMenu?.map((childmenu, j) => (
                <li key={`child_${j}`}>
                  <Link
                    to={`/${childmenu.link}`}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <span>{translateLabel(childmenu.label)}</span>
                    {childmenu.is_notify_count > 0 && (
                      <span style={notifyBadgeStyle}>{formatCount(childmenu.is_notify_count)}</span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </li>
        );
      }
    })}
  </ul>
</div>

      </SimpleBar>
    </React.Fragment>
  );
};

SidebarContent.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
  i18n: PropTypes.any,
};

export default withRouter(withTranslation()(SidebarContent));
